import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { phoneHref, waHref } from '../services/firebase';
import { downloadCsv } from '../utils/exportCsv';

export default function DriverLeaderboard({ drivers = [], rides = [] }) {
  const [timeRange, setTimeRange] = useState('week'); // 'week' | 'month' | 'all'

  // Aggregate Driver Performance
  const leaderboard = useMemo(() => {
    const now = Date.now();
    const map = {};

    // Initialize all drivers
    drivers.forEach((d) => {
      map[d.id] = {
        id: d.id,
        name: d.name || 'Anna',
        phone: d.phone || d.phoneNumber || '',
        vehicleNumber: d.vehicleNumber || 'Auto',
        isApproved: d.isApproved,
        isBlocked: d.isBlocked,
        rawDriver: d,
        totalCompletedRides: 0,
        totalGross: 0,
        ratedRidesCount: 0,
        ratingSum: 0,
        fiveStarCount: 0,
        cancelledCount: 0,
        assignedCount: 0,
      };
    });

    // Process Rides
    rides.forEach((r) => {
      if (!r.driverId || !map[r.driverId]) return;

      const rideTime = r.createdAt?.toDate ? r.createdAt.toDate().getTime() : (r.createdAt ? new Date(r.createdAt).getTime() : now);
      if (timeRange === 'week' && now - rideTime > 7 * 24 * 60 * 60 * 1000) return;
      if (timeRange === 'month' && now - rideTime > 30 * 24 * 60 * 60 * 1000) return;

      const d = map[r.driverId];
      d.assignedCount += 1;

      if (r.status === 'completed') {
        d.totalCompletedRides += 1;
        d.totalGross += Number(r.fare || 0);

        if (r.rating) {
          d.ratedRidesCount += 1;
          d.ratingSum += Number(r.rating);
          if (Number(r.rating) === 5) d.fiveStarCount += 1;
        }
      } else if (r.status === 'cancelled' && r.cancelledBy === 'driver') {
        d.cancelledCount += 1;
      }
    });

    // Calculate Averages & Sort by Completed Rides desc, then real rating
    return Object.values(map)
      .map((d) => {
        // When viewing all-time, ensure driver's true database lifetime totals are not shadowed by memory slice
        if (timeRange === 'all') {
          const raw = d.rawDriver;
          d.totalCompletedRides = Math.max(
            d.totalCompletedRides,
            Number(raw?.totalCompletedRides) || 0,
            Number(raw?.lifetimeRides) || 0,
            Number(raw?.completedRides) || 0,
            Number(raw?.totalRides) || 0
          );
          if (Number(raw?.totalFareEarnings) > 0) {
            d.totalGross = Math.max(d.totalGross, Number(raw.totalFareEarnings));
          }
        }

        const avgRating = d.ratedRidesCount > 0
          ? Number((d.ratingSum / d.ratedRidesCount).toFixed(1))
          : (Number(d.rawDriver?.ratingAvg) ? Number(d.rawDriver.ratingAvg) : null);
        const fiveStarPct = d.ratedRidesCount > 0 ? Math.round((d.fiveStarCount / d.ratedRidesCount) * 100) : null;
        const completionRate = d.assignedCount > 0 ? Math.round((d.totalCompletedRides / d.assignedCount) * 100) : null;

        return {
          ...d,
          avgRating,
          fiveStarPct,
          completionRate,
        };
      })
      .sort((a, b) => b.totalCompletedRides - a.totalCompletedRides || (b.avgRating || 0) - (a.avgRating || 0));
  }, [drivers, rides, timeRange]);

  const champion = leaderboard[0] && leaderboard[0].totalCompletedRides > 0 ? leaderboard[0] : null;

  // Copy WhatsApp announcement for the winner
  const handleAnnounceWinner = (winner) => {
    if (!winner) {
      toast.error('No rides completed yet in this time frame.');
      return;
    }

    const msg = `🏆 *MyTownly Rides - ఈ వారం అగ్రశ్రేణి అన్న (Champion Driver)!* 🏆\n\nఈ వారం అత్యధిక రైడ్లు పూర్తి చేసి ప్రయాణికుల నుండి 5-Star రేటింగ్స్ సాధించిన విజేత:\n\n🥇 *${winner.name}* (${winner.vehicleNumber})\n🛺 పూర్తయిన రైడ్లు: *${winner.totalCompletedRides} Rides*\n⭐ రేటింగ్: *${winner.avgRating} ★ (${winner.fiveStarPct}% 5-Star)*\n💰 మొత్తం సంపాదన: *₹${winner.totalGross}*\n\nఅభినందనలు అన్న! మీ అంకితభావానికి MyTownly టీమ్ తరపున ప్రత్యేక బహుమతి! 🎁🚀\n\n- MyTownly Piler టీమ్`;

    navigator.clipboard.writeText(msg).then(() => {
      toast.success(`Announcement copied for ${winner.name}! Paste into WhatsApp group.`, { duration: 4000 });
    }).catch(() => {
      toast.error('Failed to copy. Check clipboard permissions.');
    });
  };

  // Export CSV
  const handleExportCsv = () => {
    const rows = leaderboard.map((d, idx) => ({
      Rank: idx + 1,
      Name: d.name,
      Phone: d.phone,
      Vehicle: d.vehicleNumber,
      CompletedRides: d.totalCompletedRides,
      GrossEarnings: `₹${d.totalGross}`,
      AvgRating: `${d.avgRating} ★`,
      FiveStarRate: `${d.fiveStarPct}%`,
      CompletionRate: `${d.completionRate}%`,
    }));

    downloadCsv(`mytownly_anna_leaderboard_${timeRange}_${Date.now()}.csv`, rows);
    toast.success('Leaderboard CSV exported!');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Top Filter Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, background: '#1e293b', padding: '14px 20px', borderRadius: 14, border: '1px solid #334155' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>Period:</span>
          <button
            type="button"
            onClick={() => setTimeRange('week')}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: timeRange === 'week' ? '#00A86B' : '#334155', color: '#fff', border: 'none',
            }}
          >
            🗓️ This Week
          </button>
          <button
            type="button"
            onClick={() => setTimeRange('month')}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: timeRange === 'month' ? '#00A86B' : '#334155', color: '#fff', border: 'none',
            }}
          >
            📅 This Month
          </button>
          <button
            type="button"
            onClick={() => setTimeRange('all')}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: timeRange === 'all' ? '#00A86B' : '#334155', color: '#fff', border: 'none',
            }}
          >
            ⭐ All Time
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleExportCsv}
            style={{
              padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer',
              background: '#334155', color: '#f8fafc', border: '1px solid #475569',
            }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Champion Spotlight Banner */}
      {champion && (
        <div style={{
          background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
          border: '2px solid #fbbf24', borderRadius: 16, padding: '20px 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16,
          boxShadow: '0 10px 30px rgba(251, 191, 36, 0.12)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
              boxShadow: '0 4px 14px rgba(251, 191, 36, 0.4)',
            }}>
              👑
            </div>
            <div>
              <span style={{ fontSize: 12, color: '#fbbf24', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                🌟 Champion of the {timeRange === 'week' ? 'Week' : timeRange === 'month' ? 'Month' : 'Platform'}
              </span>
              <h2 style={{ fontSize: 24, fontWeight: 900, color: '#f8fafc', margin: '2px 0' }}>
                {champion.name} <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 600 }}>({champion.vehicleNumber})</span>
              </h2>
              <div style={{ display: 'flex', gap: 14, fontSize: 13, color: '#cbd5e1', marginTop: 4 }}>
                <span>🛺 <strong>{champion.totalCompletedRides}</strong> Trips Done</span>
                <span>⭐ <strong>{champion.avgRating != null ? `${champion.avgRating} ★` : 'New (Unrated)'}</strong></span>
                <span>💰 <strong>₹{champion.totalGross}</strong> Gross</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleAnnounceWinner(champion)}
            style={{
              padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: 'pointer',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color: '#fff', border: 'none',
              display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 14px rgba(37, 211, 102, 0.3)',
            }}
          >
            <span>📢 Announce Winner on WhatsApp Group</span>
          </button>
        </div>
      )}

      {/* Leaderboard Table */}
      <div style={{ background: '#1e293b', borderRadius: 14, border: '1px solid #334155', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#f8fafc', margin: 0 }}>
            🏆 Anna Fleet Rankings ({leaderboard.length} Drivers)
          </h3>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13, color: '#cbd5e1' }}>
            <thead>
              <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155', color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <th style={{ padding: '12px 16px' }}>Rank</th>
                <th style={{ padding: '12px 16px' }}>Anna (Driver)</th>
                <th style={{ padding: '12px 16px' }}>Vehicle</th>
                <th style={{ padding: '12px 16px' }}>Completed Trips</th>
                <th style={{ padding: '12px 16px' }}>Gross Revenue</th>
                <th style={{ padding: '12px 16px' }}>Avg Rating</th>
                <th style={{ padding: '12px 16px' }}>5★ Rate</th>
                <th style={{ padding: '12px 16px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((d, index) => {
                const rank = index + 1;
                const isTop3 = rank <= 3 && d.totalCompletedRides > 0;
                const rankBadge = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;

                return (
                  <tr
                    key={d.id}
                    style={{
                      borderBottom: '1px solid #334155',
                      background: rank === 1 ? 'rgba(251, 191, 36, 0.05)' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '14px 16px', fontWeight: 800, fontSize: isTop3 ? 16 : 13, color: isTop3 ? '#fbbf24' : '#94a3b8' }}>
                      {rankBadge}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <strong style={{ color: '#f8fafc', display: 'block', fontSize: 14 }}>{d.name}</strong>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{d.phone}</span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: '#0f172a', padding: '4px 8px', borderRadius: 6, border: '1px solid #334155', fontWeight: 700, fontSize: 12 }}>
                        {d.vehicleNumber}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <strong style={{ color: '#00ff9d', fontSize: 15 }}>{d.totalCompletedRides}</strong>
                      <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>trips</span>
                    </td>
                    <td style={{ padding: '14px 16px', fontWeight: 700, color: '#f8fafc' }}>
                      ₹{d.totalGross}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ color: d.avgRating != null ? '#fbbf24' : '#94a3b8', fontWeight: 800 }}>
                        {d.avgRating != null ? `★ ${d.avgRating}` : '⭐ New'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ color: d.fiveStarPct >= 90 ? '#10b981' : '#cbd5e1', fontWeight: 700 }}>
                        {d.fiveStarPct != null ? `${d.fiveStarPct}%` : '—'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {d.phone && (
                          <a
                            href={phoneHref(d.phone)}
                            style={{ padding: '4px 8px', borderRadius: 6, background: '#334155', color: '#38bdf8', textDecoration: 'none', fontSize: 12 }}
                            title="Call Anna"
                          >
                            📞
                          </a>
                        )}
                        {d.phone && (
                          <a
                            href={waHref(d.phone, `నమస్కారం ${d.name} అన్నా! MyTownly Rides నుంచి...`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ padding: '4px 8px', borderRadius: 6, background: '#128C7E', color: '#fff', textDecoration: 'none', fontSize: 12 }}
                            title="WhatsApp Anna"
                          >
                            💬
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
